import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  ALL_DIAL_CODES,
  PRIORITY_CODES_SET,
  PRIORITY_DIAL_CODES,
} from "../../../constants/phoneDialCodes";
import { getPhoneLengthRule } from "../../utils/contactFieldValidation";
import { caretPosFromDigitsCount, formatNationalPhone } from "../../utils/formatNationalPhone";
import { parsePhoneInputValue } from "../../utils/splitDialNational";

export interface DialCodeItem {
  code: string;
  name: string;
  flag?: string;
}

function flagToIso2(flag: string | undefined): string {
  const parts = [...String(flag || "")];
  if (parts.length < 2) return "";
  const base = 0x1f1e6;
  const a = parts[0].codePointAt(0);
  const b = parts[1].codePointAt(0);
  if (!a || !b) return "";
  if (a < base || a > base + 25 || b < base || b > base + 25) return "";
  return String.fromCharCode(a - base + 65, b - base + 65).toLowerCase();
}

function getCountryIso2(item: DialCodeItem | null | undefined): string {
  const fromFlag = flagToIso2(item?.flag);
  if (fromFlag) return fromFlag;
  if (item?.code === "+1") return "us";
  return "un";
}

/** Resolve dial entry when several countries share one code (e.g. +1 US/CA). */
function resolveDialItem(countryCode: string, preferredIso: string | null): DialCodeItem | null {
  const code = countryCode || "+34";
  const pool = [...PRIORITY_DIAL_CODES, ...ALL_DIAL_CODES];
  const matches = pool.filter((c) => c.code === code);
  if (!matches.length) return null;
  if (preferredIso) {
    const byIso = matches.find((c) => getCountryIso2(c) === preferredIso);
    if (byIso) return byIso;
  }
  // Default +1 to United States (not Canada, which appears earlier alphabetically).
  if (code === "+1") {
    const us = matches.find((c) => getCountryIso2(c) === "us");
    if (us) return us;
  }
  return matches[0];
}

const FLAG_BOX_STYLE = { width: "1.1rem", height: "0.82rem" } as const;

export interface PhoneDialNationalInputProps {
  /** E.g. "+34" */
  countryCode: string;
  onCountryCodeChange: (code: string) => void;
  nationalNumber: string;
  onNationalNumberChange: (value: string) => void;
  placeholder?: string;
  /** When false, removes focus ring / emerald “filled” border so read-only edit mode matches plain fields */
  showFilledState?: boolean;
  /** `dark` matches org / Spain signup pages (Signup.css variables) */
  theme?: "light" | "dark";
  /** Applied to the outer wrapper */
  className?: string;
  invalid?: boolean;
}

/**
 * Country dial dropdown + national number input (same pattern as representative phone / login).
 */
export default function PhoneDialNationalInput({
  countryCode,
  onCountryCodeChange,
  nationalNumber,
  onNationalNumberChange,
  placeholder = "",
  showFilledState = true,
  theme = "light",
  className = "",
  invalid = false,
}: PhoneDialNationalInputProps) {
  const [showDial, setShowDial] = useState(false);
  const [dialSearch, setDialSearch] = useState("");
  const [selectedIso, setSelectedIso] = useState<string | null>(null);
  const dialRef = useRef<HTMLDivElement>(null);
  const nationalInputRef = useRef<HTMLInputElement>(null);
  const pendingCaretDigitsRef = useRef<number | null>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dialRef.current && !dialRef.current.contains(e.target as Node)) {
        setShowDial(false);
        setDialSearch("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filtered = dialSearch
    ? ALL_DIAL_CODES.filter(
        (c) =>
          c.code.includes(dialSearch) ||
          c.name.toLowerCase().includes(dialSearch.toLowerCase())
      )
    : ALL_DIAL_CODES.filter(
        (c) => !PRIORITY_CODES_SET.has(c.code + c.name.split(" /")[0])
      );

  const currentFlag = useMemo(
    () => resolveDialItem(countryCode, selectedIso),
    [countryCode, selectedIso]
  );

  const currentIso = getCountryIso2(currentFlag);

  const maxNationalDigits = useMemo(() => {
    return getPhoneLengthRule(countryCode || "+34").max;
  }, [countryCode]);

  const handleSelectCode = (c: DialCodeItem) => {
    setSelectedIso(getCountryIso2(c));
    onCountryCodeChange(c.code);
    setShowDial(false);
    setDialSearch("");
  };

  const isSelected = (c: DialCodeItem) =>
    c.code === countryCode && getCountryIso2(c) === currentIso;

  const applyRawPhoneValue = (rawValue: string, caretInRaw: number | null = null) => {
    const caret = caretInRaw == null ? String(rawValue || "").length : caretInRaw;
    pendingCaretDigitsRef.current = String(rawValue || "")
      .slice(0, Math.max(0, caret))
      .replace(/\D/g, "").length;

    const { dial, national } = parsePhoneInputValue(rawValue, countryCode || "+34");
    const nextMax = getPhoneLengthRule(dial || "+34").max;
    onCountryCodeChange(dial);
    onNationalNumberChange(national.slice(0, nextMax));
  };

  const hasNumber = Boolean(String(nationalNumber ?? "").trim());
  const isDark = theme === "dark";
  const formattedNational = formatNationalPhone(nationalNumber, countryCode || "+34");

  useLayoutEffect(() => {
    const input = nationalInputRef.current;
    const desiredDigits = pendingCaretDigitsRef.current;
    if (!input || desiredDigits === null) return;
    const nextPos = caretPosFromDigitsCount(formattedNational, desiredDigits);
    try {
      input.setSelectionRange(nextPos, nextPos);
    } catch {
      // ignore
    } finally {
      pendingCaretDigitsRef.current = null;
    }
  }, [formattedNational]);

  const rowClass = isDark
    ? `phone-dial-national__row${showDial ? " phone-dial-national__row--open" : ""}${invalid ? " phone-dial-national__row--invalid" : ""}`
    : `flex h-12 w-full min-w-0 bg-white border rounded-xl transition-all ${
        showDial
          ? "border-emerald-400 ring-2 ring-emerald-300"
          : showFilledState && hasNumber
            ? "border-emerald-400 focus-within:ring-2 focus-within:ring-emerald-300"
            : "border-slate-200 focus-within:border-emerald-400 focus-within:ring-2 focus-within:ring-emerald-300"
      }`;

  const dialBtnClass = isDark
    ? "phone-dial-national__dial-btn"
    : "h-full pl-3 pr-2.5 flex items-center gap-2 border-r border-slate-200 bg-slate-50 cursor-pointer hover:bg-slate-100 transition-colors whitespace-nowrap shrink-0 rounded-l-xl";

  const inputClass = isDark
    ? "phone-dial-national__input placeholder:text-white/30"
    : "min-w-0 flex-1 px-3 text-slate-900 placeholder:text-slate-400 focus:outline-none bg-transparent rounded-r-xl";

  const menuClass = isDark
    ? "phone-dial-national__menu"
    : "absolute top-[calc(100%+4px)] left-0 w-72 bg-white border border-slate-200 rounded-xl z-50 overflow-hidden";

  const menuSearchWrapClass = isDark
    ? "phone-dial-national__menu-search-wrap"
    : "p-2 border-b border-slate-100";

  const menuSearchInnerClass = isDark
    ? "phone-dial-national__menu-search-inner"
    : "flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-lg";

  const menuSearchInputClass = isDark
    ? "phone-dial-national__menu-search-input"
    : "flex-1 bg-transparent text-slate-900 placeholder:text-slate-400 focus:outline-none";

  const menuItemClass = (selected: boolean) =>
    isDark
      ? `phone-dial-national__menu-item${selected ? " phone-dial-national__menu-item--selected" : ""}`
      : `w-full text-left px-3 py-2.5 text-sm flex items-center gap-3 transition-colors cursor-pointer ${
          selected ? "bg-slate-100" : "hover:bg-slate-50"
        }`;

  const rootClass = isDark
    ? `phone-dial-national phone-dial-national--dark ${className}`.trim()
    : `relative w-full min-w-0 ${className}`.trim();

  return (
    <div ref={dialRef} className={rootClass}>
      <div
        className={rowClass}
        style={isDark ? undefined : { overflow: "visible" }}
      >
        <button
          type="button"
          onClick={() => setShowDial((v) => !v)}
          className={dialBtnClass}
        >
          <span
            className={`fi fi-${getCountryIso2(currentFlag)} rounded-sm shrink-0`}
            style={FLAG_BOX_STYLE}
            aria-hidden
          />
          <span className={isDark ? "phone-dial-national__dial-code" : "text-[13px] font-semibold text-slate-700"}>
            {countryCode || "+34"}
          </span>
          <i className={`ri-arrow-${showDial ? "up" : "down"}-s-line ${isDark ? "phone-dial-national__dial-arrow" : "text-slate-400 text-xs"}`} />
        </button>
        <input
          ref={nationalInputRef}
          type="tel"
          inputMode="numeric"
          maxLength={Math.max(maxNationalDigits + 8, formattedNational.length || 0)}
          value={formattedNational}
          onChange={(e) => applyRawPhoneValue(e.target.value, e.target.selectionStart)}
          onPaste={(e) => {
            const pasted = e.clipboardData.getData("text");
            if (!pasted) return;
            e.preventDefault();
            applyRawPhoneValue(pasted);
          }}
          placeholder={placeholder}
          style={{ fontSize: "16px" }}
          className={inputClass}
          autoComplete="tel-national"
        />
      </div>
      {showDial && (
        <div
          className={menuClass}
          style={isDark ? undefined : { boxShadow: "0 4px 20px rgba(0,0,0,0.10)" }}
        >
          <div className={menuSearchWrapClass}>
            <div className={menuSearchInnerClass}>
              <i className={`ri-search-line ${isDark ? "phone-dial-national__search-icon" : "text-slate-400 text-sm"}`} />
              <input
                type="text"
                value={dialSearch}
                onChange={(e) => setDialSearch(e.target.value)}
                placeholder="Search country or code…"
                autoFocus
                style={{ fontSize: "16px", touchAction: "manipulation" }}
                className={menuSearchInputClass}
              />
              {dialSearch && (
                <button type="button" onClick={() => setDialSearch("")} className="cursor-pointer">
                  <i className={`ri-close-line ${isDark ? "phone-dial-national__search-icon" : "text-slate-400 text-sm"}`} />
                </button>
              )}
            </div>
          </div>
          <div className={isDark ? "phone-dial-national__menu-list" : "max-h-56 overflow-y-auto"}>
            {!dialSearch && (
              <>
                {PRIORITY_DIAL_CODES.map((c) => (
                  <button
                    key={`p-${c.code}-${c.name}`}
                    type="button"
                    onClick={() => handleSelectCode(c)}
                    className={menuItemClass(isSelected(c))}
                  >
                    <span
                      className={`fi fi-${getCountryIso2(c)} rounded-sm shrink-0`}
                      style={FLAG_BOX_STYLE}
                      aria-hidden
                    />
                    <span className={isDark ? "phone-dial-national__menu-code" : "font-semibold w-10 shrink-0 text-slate-800 text-[13px]"}>
                      {c.code}
                    </span>
                    <span className={isDark ? "phone-dial-national__menu-name" : "text-slate-500 truncate text-[13px]"}>
                      {c.name}
                    </span>
                  </button>
                ))}
                <div className={isDark ? "phone-dial-national__menu-divider" : "border-t border-slate-100 my-1"} />
              </>
            )}
            {filtered.length === 0 ? (
              <div className={isDark ? "phone-dial-national__menu-empty" : "px-4 py-3 text-sm text-slate-400 text-center"}>
                No results
              </div>
            ) : (
              filtered.map((c) => (
                <button
                  key={`a-${c.code}-${c.name}`}
                  type="button"
                  onClick={() => handleSelectCode(c)}
                  className={menuItemClass(isSelected(c))}
                >
                  <span
                    className={`fi fi-${getCountryIso2(c)} rounded-sm shrink-0`}
                    style={FLAG_BOX_STYLE}
                    aria-hidden
                  />
                  <span className={isDark ? "phone-dial-national__menu-code" : "font-semibold w-10 shrink-0 text-slate-800 text-[13px]"}>
                    {c.code}
                  </span>
                  <span className={isDark ? "phone-dial-national__menu-name" : "text-slate-500 truncate text-[13px]"}>
                    {c.name}
                  </span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
